// src/components/UnifiedLogin.jsx
import React, { useState, useEffect } from 'react';
import '../styles/Login.css';
import logo from '../logo512.png';
import { apiFetch } from '../api/apiClient';
import LoginCodeStep from './LoginCodeStep';
import {
  isBiometricAvailable, isBiometricEnabled,
  enableBiometricLogin, disableBiometricLogin, loginWithBiometric,
  getBiometricLabel, getBiometricUsername,
} from '../utils/biometricAuth';

const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="#33565C" strokeWidth="1.5">
    <path d="M1.8 10S4.9 4.6 10 4.6 18.2 10 18.2 10 15.1 15.4 10 15.4 1.8 10 1.8 10z"></path>
    <circle cx="10" cy="10" r="2.4"></circle>
  </svg>
);

const UnifiedLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEnablePrompt, setShowEnablePrompt] = useState(false);
  const [canUseFaceId, setCanUseFaceId] = useState(false);
  // "Face ID" on iPhone, "fingerprint" / "face unlock" on Android
  const [bioLabel, setBioLabel] = useState('Face ID');
  // Account the saved biometric login belongs to (shown under the button)
  const [bioUser, setBioUser] = useState(getBiometricUsername());
  const [pendingCreds, setPendingCreds] = useState(null); // { username, refreshToken, user }
  const [challenge, setChallenge] = useState(null); // set once the password is accepted

  useEffect(() => {
    (async () => {
      const available = await isBiometricAvailable();
      setCanUseFaceId(available && isBiometricEnabled());
      if (available) setBioLabel(await getBiometricLabel());
    })();
  }, []);

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === 'Enter' && !isLoading && !challenge && (username && password)) {
        const form = e.target.closest('form');
        if (form) {
          const submitEvent = new Event('submit', { cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      }
    };
    document.addEventListener('keydown', handleEnterKey);
    return () => document.removeEventListener('keydown', handleEnterKey);
  }, [isLoading, username, password, challenge]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      setError('Please enter your username or email, and your password');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await apiFetch('/api/auth/mobile/login', {
        method: 'POST',
        skipAuthRefresh: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid credentials');
      }

      if (data.mfa_required) {
        setChallenge(data); // password OK - now ask for the emailed code
        return;
      }
      await completeLogin(data); // server has MFA_ENABLED=false
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Unified login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Runs only after password + code, so Face ID is never offered to someone
  // who only knows the password.
  const completeLogin = async (data) => {
    setChallenge(null);
    const canOfferFaceId = await isBiometricAvailable();
    const savedUser = getBiometricUsername();

    // Offer it when nothing is saved yet, or when the saved login belongs to
    // a different account (e.g. Face ID is set up for A, B just logged in).
    const isDifferentAccount = isBiometricEnabled() && savedUser !== data.user.username;

    if (canOfferFaceId && (!isBiometricEnabled() || isDifferentAccount)) {
      setPendingCreds({
        username: data.user.username,
        refreshToken: data.refresh_token,
        user: data.user,
      });
      setShowEnablePrompt(true);
    } else {
      onLogin(data.user);
    }
  };

  const confirmEnableFaceId = async () => {
    await enableBiometricLogin(pendingCreds.username, pendingCreds.refreshToken);
    setBioUser(pendingCreds.username);
    const user = pendingCreds.user;
    setShowEnablePrompt(false);
    setPendingCreds(null);
    onLogin(user);
  };

  const skipEnableFaceId = () => {
    const user = pendingCreds.user;
    setShowEnablePrompt(false);
    setPendingCreds(null);
    onLogin(user);
  };

  const handleFaceIdLogin = async () => {
    setError('');
    try {
      const user = await loginWithBiometric();
      onLogin(user);
    } catch (err) {
      setCanUseFaceId(false); // fall back to the password form
      setError(err.message || `Login with ${bioLabel} failed`);
    }
  };

  const turnOffFaceId = async () => {
    await disableBiometricLogin();
    setCanUseFaceId(false);
    setBioUser(null);
    setError('');
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  if (challenge) {
    return (
      <LoginCodeStep
        challenge={challenge}
        containerClass="unified-login"
        onVerified={completeLogin}
        onCancel={() => { setChallenge(null); setPassword(''); }}
      />
    );
  }

  if (showEnablePrompt) {
    // "Face ID" / "Touch ID" read fine alone; "fingerprint" needs "login".
    const methodName = bioLabel === 'Face ID' || bioLabel === 'Touch ID' ? bioLabel : `${bioLabel} login`;
    const savedUser = getBiometricUsername();
    const isSwitch = isBiometricEnabled() && savedUser && savedUser !== pendingCreds?.username;
    return (
      <div className="login-page">
        <div className="login-container unified-login">
          <div className="login-header">
            <img src={logo} className="login-logo-247" alt="247 Logo" />
            <div>
              {isSwitch ? (
                <>
                  <h2>Switch {methodName} to {pendingCreds.username}?</h2>
                  <p>
                    {methodName} on this device currently logs in as <strong>{savedUser}</strong>.
                    Only one account can use it at a time.
                  </p>
                </>
              ) : (
                <>
                  <h2>Enable {methodName}?</h2>
                  <p>Log in faster next time using {bioLabel} instead of your password.</p>
                </>
              )}
            </div>
          </div>
          <button className="login-btn" onClick={confirmEnableFaceId}>
            {isSwitch ? `Switch to ${pendingCreds.username}` : `Enable ${methodName}`}
          </button>
          <button className="login-btn login-btn-secondary" onClick={skipEnableFaceId}>
            {isSwitch ? `Keep ${savedUser}` : 'Not now'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container unified-login">
        <div className="login-header">
          <img src={logo} className="login-logo-247" alt="247 Logo" />
          <div>
            <h2>Twenty4Seven Portal</h2>
            <p>Central Monitoring System</p>
          </div>
        </div>

        {error && (
          <div className="login-error">
            <span className="login-error-icon">!</span>
            <div>{error}</div>
          </div>
        )}

        {canUseFaceId && (
          <>
            <button type="button" className="login-btn" onClick={handleFaceIdLogin}>
              Log in with {bioLabel}
            </button>
            <div className="login-bio-account">
              {bioUser ? <>as <strong>{bioUser}</strong> · </> : null}
              <button type="button" className="login-link-btn" onClick={turnOffFaceId}>
                Turn off {bioLabel}
              </button>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username or email</label>
            <input
              type="text"
              id="username"
              autoComplete="username"
              autoCapitalize="none"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="form-group password-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon />
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-btn"
            disabled={isLoading || !username.trim() || !password.trim()}
          >
            {isLoading ? (<><span className="spinner-icon">↻</span>Logging in...</>) : 'Login'}
          </button>
        </form>

        <div className="login-info">
          <p>Need help? Contact your system administrator<br />
          <a href="mailto:support@twenty4seven.io">support@twenty4seven.io</a></p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLogin;
